import clsx from 'clsx';
import TextEditorUntyped from 'core/modules/compositions/wysiwyg/TextEditor';
import React from 'react';
import type { DialogSize } from '../Dialog/Dialog';
import Dialog from '../Dialog/Dialog';
import useDialogRichTextInput from './useDialogRichTextInput';

// TextEditor is a legacy JS composition wrapped in `withTranslation()`; the
// resulting TS surface drops the prop typing. We narrow it locally to the
// subset of props this organism uses.
type TextEditorProps = Readonly<{
  disabled?: boolean;
  extraConfig?: Record<string, unknown>;
  model?: string;
  onModelChange?: (value: string) => void;
  placeholder?: string;
}>;

const TextEditor = TextEditorUntyped as unknown as React.ComponentType<TextEditorProps>;

/**
 * DialogRichTextInput — v2 organism.
 *
 * Modal de edicion de texto enriquecido construido sobre {@link Dialog}.
 * Unifica los dos dialogs v1 (`DialogEditName` con `size="s"` y validacion
 * por `minLength`, y `DialogTextEditor` con `size="l"`, toolbar `lite` y
 * `quickInsert` habilitado) en un unico organism del Design System v2 con
 * una API explicita y consistente.
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - Hereda de `Dialog` v2: focus trap, cierre por `Escape`, `aria-modal`,
 *    `aria-labelledby` apuntando al titulo y devolucion de foco al trigger
 *    cuando se cierra.
 *  - Cuando `onCancel` no se pasa, el dialog reutiliza `onClose` como
 *    handler de cancelacion para garantizar que el boton "Cancel" siempre
 *    tenga una accion valida.
 *  - El boton principal queda deshabilitado mientras `value.length` no
 *    alcance `minLength`, evitando confirmar entradas vacias o demasiado
 *    cortas.
 */

export type DialogRichTextInputProps = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  className?: string;
  editorMinHeight?: string;
  hasQuickInsert?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  minLength?: number;
  onCancel?: () => void;
  onChange: (value: string) => void;
  onClose: () => void;
  onSuccess?: () => void;
  placeholder?: string;
  size?: DialogSize;
  title: string;
  value: string;
}>;

function DialogRichTextInput({
  buttonTextCancel,
  buttonTextSuccess,
  className,
  editorMinHeight,
  hasQuickInsert = false,
  isLoading = false,
  isOpen,
  minLength = 0,
  onCancel,
  onChange,
  onClose,
  onSuccess,
  placeholder,
  size = 's',
  title,
  value,
}: DialogRichTextInputProps): React.JSX.Element {
  const {
    editorStyle,
    extraConfig,
    isDisabledSuccess,
    resolvedButtonTextCancel,
    resolvedButtonTextSuccess,
    resolvedOnCancel,
    resolvedPlaceholder,
  } = useDialogRichTextInput({ buttonTextCancel, buttonTextSuccess, editorMinHeight, hasQuickInsert, minLength, onCancel, onClose, placeholder, value });

  return (
    <Dialog
      buttonTextCancel={resolvedButtonTextCancel}
      buttonTextSuccess={resolvedButtonTextSuccess}
      className={clsx('dialog-rich-text-input', className)}
      isDisabledSuccess={isDisabledSuccess}
      isLoading={isLoading}
      isOpen={isOpen}
      onCancel={resolvedOnCancel}
      onClose={onClose}
      onSuccess={onSuccess}
      size={size}
      title={title}
    >
      <div className="dialog-rich-text-input__editor" style={editorStyle}>
        <TextEditor extraConfig={extraConfig} model={value} onModelChange={onChange} placeholder={resolvedPlaceholder} />
      </div>
    </Dialog>
  );
}

export default DialogRichTextInput;
